import { CycleQuestionKinds } from "#shared/cycleQuestionSchemas.ts";
import { NineBoxCellIds } from "#shared/nineBox.ts";
import { DEFAULT_SCORE_BUCKET_GRANULARITY, ScoreBucketGranularities } from "#shared/scoreBuckets.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { parseAsFloat, parseAsString, parseAsStringEnum, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useMemo } from "react";
import { useLocation } from "react-router";
import { useRevieweeFilters } from "../../reviewee-filters/useRevieweeFilters.ts";
import { resolveActiveTab, resolveCycleExports, type CycleDetailTab, type CycleExportGroup } from "./resolveCycleExports.ts";
import { useRosterFilters } from "./useRosterFilters.ts";

const exportQuerySchema = {
    sort: parseAsString,
    dir: parseAsStringLiteral(["asc", "desc"] as const),
    buckets: parseAsStringEnum([...ScoreBucketGranularities]).withDefault(DEFAULT_SCORE_BUCKET_GRANULARITY),
    band: parseAsFloat,
    q: parseAsString.withDefault(""),
    cell: parseAsStringEnum([...NineBoxCellIds]),
    questionType: parseAsStringEnum([...CycleQuestionKinds])
};

export interface UseCycleExportsResult {
    groups: CycleExportGroup[];
    tab: CycleDetailTab;
}

export function useCycleExports(cycleId: string): UseCycleExportsResult {
    const session = useSession();
    const { pathname } = useLocation();
    const [{ sort, dir, buckets, band, q, cell, questionType }] = useQueryStates(exportQuerySchema);
    const { memberFilters } = useRevieweeFilters();
    const { rosterFilters } = useRosterFilters();

    const orgId = session?.organizationId ?? "";
    const tab = resolveActiveTab(pathname);

    const groups = useMemo(() => resolveCycleExports({ tab, orgId, cycleId, sort, dir, granularity: buckets, band, searchTerm: q, cellId: cell, questionType, memberFilters, rosterFilters }), [tab, orgId, cycleId, sort, dir, buckets, band, q, cell, questionType, memberFilters, rosterFilters]);

    return { groups, tab };
}
